import React from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DosenButton from '../ui/Buttons/DosenButton';

// Reusable confirmation modal in the app's dark surface style. Used by the
// program-lock flows (confirm lock/unlock in the designer; "program is locked —
// unlock?" warning on the members page) and generic enough for other confirms.

const SURFACE = '#1C1C1C';
const BORDER = '#2A2A2A';
const TEXT_PRIMARY = '#FDFDFD';
const TEXT_SECONDARY = '#B6B6B6';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  // Confirm label while busy, e.g. "Deleting...". Buttons show text, not a spinner.
  busyLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busy = false,
  busyLabel,
  onConfirm,
  onClose
}) => {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      PaperProps={{
        sx: {
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: '12px',
          width: 420,
          maxWidth: '92vw',
          p: 0
        }
      }}
    >
      <Box sx={{ p: '20px 22px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 16,
              fontWeight: 700,
              color: TEXT_PRIMARY,
              pr: 1
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={busy}
            size="small"
            sx={{ color: TEXT_SECONDARY, mt: '-4px', mr: '-6px' }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={{ mt: '10px' }}>
          {typeof message === 'string' ? (
            <Typography
              sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13.5, lineHeight: '20px', color: TEXT_SECONDARY }}
            >
              {message}
            </Typography>
          ) : (
            message
          )}
        </Box>

        <Box sx={{ mt: '22px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <DosenButton tone="basic" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </DosenButton>
          <DosenButton
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            loadingText={busyLabel ?? confirmLabel}
            sx={{ minWidth: 96 }}
          >
            {confirmLabel}
          </DosenButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConfirmDialog;
